import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PureQueryOptions } from '@apollo/client';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import { useCreateTaskFromMsgMutation } from 'core/graphql';

import { useToasts } from '../Toast/useToasts';
import { useStaffUsers, IStaffUser } from '../Students/useStaffUsers';

export interface ILinkedTask {
  id: string;
  title: string;
  status: string;
}

export interface ICreateTaskFromMessageArgs {
  messageId: string;
  title: string;
  description?: string;
  dueDate?: string;
  /** Omit to let the backend default the assignee to the current user. */
  assigneeId?: string;
  priority?: string;
  /**
   * Fallback group for DM conversations opened from a group feed — the task
   * then lands on that group's "Задачи" tab. Ignored when the conversation
   * already carries a group.
   */
  groupId?: string;
}

export interface ICreatedFromMessage {
  id: string;
  isHandled: boolean;
  linkedTask: ILinkedTask | null;
}

interface IUseTaskFromMessage {
  staffUsers: IStaffUser[];
  staffLoading: boolean;
  creating: boolean;
  /** Returns the updated message on success, null on failure (toast shown). */
  createTask: (
    args: ICreateTaskFromMessageArgs,
    refetchQueries?: PureQueryOptions[],
  ) => Promise<ICreatedFromMessage | null>;
}

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * "Create a task from a student message" (AM center). Wraps the staff-user
 * picker query + the createTaskFromMessage mutation with toasts. The backend
 * inherits student/group from the message's conversation, defaults the
 * assignee to the current user, marks the message handled and links the task.
 */
export const useTaskFromMessage = (skipStaff = false): IUseTaskFromMessage => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { staffUsers, loading: staffLoading } = useStaffUsers(skipStaff);

  const [mutate, { loading: creating }] = useCreateTaskFromMsgMutation();

  const createTask = useCallback(
    async (
      args: ICreateTaskFromMessageArgs,
      refetchQueries?: PureQueryOptions[],
    ): Promise<ICreatedFromMessage | null> => {
      try {
        const { data } = await mutate({
          variables: {
            messageId: args.messageId,
            title: args.title,
            description: args.description || undefined,
            dueDate: args.dueDate || undefined,
            assigneeId: args.assigneeId || undefined,
            priority: args.priority || undefined,
            groupId: args.groupId || undefined,
          },
          // The payload carries task + message + conversation, which merge
          // into the cache. Callers still pass `badgeRefetch` where group
          // badges are on screen: this also ticks the message handled, and
          // groupCommsIndex is an aggregate no payload can patch (round 3).
          refetchQueries: refetchQueries || [],
        });
        const payload = data?.createTaskFromMessage;
        if (payload?.success && payload.message) {
          addToast({
            data: {
              type: ToastTypes.success,
              title: t('pages.communications.taskCreated'),
              text: payload.task?.title || undefined,
            },
            type: GlobalToastTypeNames.FakturowniaMatch,
          });
          return {
            id: payload.message.id,
            isHandled: !!payload.message.isHandled,
            linkedTask: payload.message.linkedTask
              ? {
                  id: payload.message.linkedTask.id,
                  title: payload.message.linkedTask.title,
                  status: payload.message.linkedTask.status || 'open',
                }
              : null,
          };
        }
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.taskCreateFailed'),
            text: errText(payload?.customErrors),
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return null;
      } catch (e) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.taskCreateFailed'),
            text: (e as Error).message,
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return null;
      }
    },
    [mutate, addToast, t],
  );

  return {
    staffUsers,
    staffLoading,
    creating,
    createTask,
  };
};
